/**
 * Central export for all Contributing Assets document generators
 */

import { generateContributionSummaryHtml } from './contributionSummary';
import { generateAuthorizationResolutionHtml } from './authorizationResolution';
import { generateAcceptanceResolutionHtml } from './acceptanceResolution';
import { generateFilingCertificateHtml } from './filingCertificate';
import { generatePrivateAssignmentHtml } from './privateAssignment';
import { generateOperatingAgreementHtml, generateOperatingAgreementShortFormHtml } from './operatingAgreement';
import { generateVehicleInstrumentHtml } from './vehicleInstrument';
import { generateIPAssignmentHtml } from './ipAssignment';
import { generateDeedRecordingCoverSheetHtml } from './deedRecordingCoverSheet';
import { generatePropertyStewardshipDocHtml } from './propertyStewardshipDoc';
import { generateDMVTransferDocHtml } from './dmvTransferDoc';
import { generateBankAccountUpdateResolutionHtml } from './bankAccountUpdateResolution';

export { getAllMergeFields, replaceMergeFields } from './mergeFields';
export {
  generateContributionSummaryHtml,
  generateAuthorizationResolutionHtml,
  generateAcceptanceResolutionHtml,
  generateFilingCertificateHtml,
  generatePrivateAssignmentHtml,
  generateOperatingAgreementHtml,
  generateOperatingAgreementShortFormHtml,
  generateVehicleInstrumentHtml,
  generateIPAssignmentHtml,
  generateDeedRecordingCoverSheetHtml,
  generatePropertyStewardshipDocHtml,
  generateDMVTransferDocHtml,
  generateBankAccountUpdateResolutionHtml,
};

/**
 * Get required documents for a given asset type and phase
 */
export interface DocumentRequirement {
  key: string;
  title: string;
  requiresSignature: boolean;
  signerRole: 'contributor' | 'treasury_secretary' | 'both';
  phase: 1 | 2;
  generator: (workflowData: any, unaData: any) => string;
}

export function getRequiredDocuments(assetType: string, phase: 1 | 2): DocumentRequirement[] {
  if (phase === 1) {
    // Phase 1: Authorization & Preparation (PITCH)
    const phase1Docs: DocumentRequirement[] = [
      {
        key: 'contribution_summary',
        title: 'Contribution Summary Document',
        requiresSignature: false,
        signerRole: 'contributor',
        phase: 1,
        generator: generateContributionSummaryHtml,
      },
      {
        key: 'authorization_resolution',
        title: 'Authorization Resolution',
        requiresSignature: true,
        signerRole: 'contributor',
        phase: 1,
        generator: generateAuthorizationResolutionHtml,
      },
    ];

    // Add asset-specific documents
    if (assetType === 'Business Entity' || assetType?.includes('Business Entity')) {
      phase1Docs.push({
        key: 'operating_agreement',
        title: 'Operating Agreement',
        requiresSignature: false,
        signerRole: 'contributor',
        phase: 1,
        generator: generateOperatingAgreementHtml,
      });
    }

    if (assetType === 'Real Estate' || assetType?.includes('Real Estate')) {
      phase1Docs.push({
        key: 'private_assignment',
        title: 'Private Member Assignment',
        requiresSignature: true,
        signerRole: 'treasury_secretary',
        phase: 1,
        generator: generatePrivateAssignmentHtml,
      });
    }

    if (assetType === 'Vehicle' || assetType?.includes('Vehicle')) {
      phase1Docs.push({
        key: 'vehicle_instrument',
        title: 'Private Member Contribution Instrument (Vehicle)',
        requiresSignature: true,
        signerRole: 'treasury_secretary',
        phase: 1,
        generator: generateVehicleInstrumentHtml,
      });
    }

    if (assetType === 'Intellectual Property') {
      phase1Docs.push({
        key: 'ip_assignment',
        title: 'Intellectual Property Assignment',
        requiresSignature: true,
        signerRole: 'treasury_secretary',
        phase: 1,
        generator: generateIPAssignmentHtml,
      });
    }

    return phase1Docs;
  }

  // Phase 2: Accept Contribution (CATCH)
  if (phase === 2) {
    const phase2Docs: DocumentRequirement[] = [
      {
        key: 'acceptance_resolution',
        title: 'Resolution of Acceptance',
        requiresSignature: true,
        signerRole: 'treasury_secretary',
        phase: 2,
        generator: generateAcceptanceResolutionHtml,
      },
      {
        key: 'filing_certificate',
        title: 'Contribution Filing Certificate',
        requiresSignature: false,
        signerRole: 'treasury_secretary',
        phase: 2,
        generator: generateFilingCertificateHtml,
      },
    ];

    // Add asset-specific Phase 2 documents
    if (assetType === 'Real Estate' || assetType?.includes('Real Estate')) {
      phase2Docs.push(
        {
          key: 'deed_recording_cover',
          title: 'Deed Recording Cover Sheet',
          requiresSignature: false,
          signerRole: 'treasury_secretary',
          phase: 2,
          generator: generateDeedRecordingCoverSheetHtml,
        },
        {
          key: 'property_stewardship',
          title: 'Property Stewardship Document',
          requiresSignature: false,
          signerRole: 'treasury_secretary',
          phase: 2,
          generator: generatePropertyStewardshipDocHtml,
        }
      );
    }

    if (assetType === 'Vehicle' || assetType?.includes('Vehicle')) {
      phase2Docs.push({
        key: 'dmv_transfer',
        title: 'DMV Transfer Instructions',
        requiresSignature: false,
        signerRole: 'treasury_secretary',
        phase: 2,
        generator: generateDMVTransferDocHtml,
      });
    }

    if (assetType === 'Cash/Bank Account' || assetType?.includes('Cash')) {
      phase2Docs.push({
        key: 'bank_account_update',
        title: 'Bank Account Update Resolution',
        requiresSignature: true,
        signerRole: 'treasury_secretary',
        phase: 2,
        generator: generateBankAccountUpdateResolutionHtml,
      });
    }

    return phase2Docs;
  }

  return [];
}
